import React from 'react';
const { useState, useMemo } = React;
import { TABLE_IDS, CURRENT_USER, getAuthHeaders } from '../../globals.js';
import {
  LINK_PRESETS, linkParams, linkUrl, FORM_PATH, LINK_FIELDS, REQUEST_FIELDS, PERSON_FIELDS, OPPORTUNITY_FIELDS,
  METHOD_LABELS, MANUAL_ORIGINS, UNKNOWN, NOTE_MAX, requestOriginOf, requestOriginLabel, firstOriginOf, manualOriginFields, opportunityFieldsFromRequest, newPeopleOfLink,
} from '../../requestOrigin.js';
import { InfoTip } from '../../utils.jsx';
import { FORM_TYPES, LINK_FORM_FIELDS, TOPIC_FIELDS, activeTopics, topicCode, topicName, eventWhen } from '../../requestForms.js';

// Where requests come from (src/requestOrigin.js): identified links to the
// public form, the origin of each request, the person's first origin and the
// opportunity created from a request. Oike only knows a link was used when a
// request arrives with it: no visits or clicks are counted.

const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 14 };
const label = { fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 };
const muted = { fontSize: 12, color: '#56625F' };
const row = { display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 13, margin: '3px 0' };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('es', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const fieldOf = (r, k) => { const v = r?.fields?.[k]; return v && typeof v === 'object' && !Array.isArray(v) ? (v.name ?? '') : (v ?? ''); };
const ids = (r, k) => (Array.isArray(r?.fields?.[k]) ? r.fields[k] : []);
const personName = (s) => [fieldOf(s, 'Name'), fieldOf(s, 'Last name')].filter(Boolean).join(' ') || fieldOf(s, 'Email') || 'Contacto';
const openContact = (id) => window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'contacts', contactId: id } }));
const authHeaders = () => getAuthHeaders();

/** The small tag on each request of the list. */
export function OriginTag({ request }) {
  const text = requestOriginLabel(request.fields || {});
  const unknown = text === UNKNOWN;
  return <span style={{ fontSize: 11, borderRadius: 4, padding: '2px 7px', background: unknown ? '#f1f5f9' : '#E6F2EF', color: unknown ? '#56625F' : '#155C54' }} title="Origen de la solicitud">{text}</span>;
}

/** Captured, declared and manual origin of one request; the manual one can be recorded here. */
export function RequestOriginPanel({ request, links, onSaved }) {
  const o = requestOriginOf(request.fields || {});
  const link = o.captured?.linkId ? links.find(l => l.id === o.captured.linkId) : null;
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState('referral');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setSaving(true); setError('');
    try {
      const res = await fetch('/api/intake-review', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ id: request.id, manualOrigin: { kind, note } }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
      onSaved(manualOriginFields({ kind, note }, 'request'), body.personUpdated ? manualOriginFields({ kind, note }, 'person') : null);
      setEditing(false); setNote('');
    } catch (e) { setError(`No se pudo guardar: ${e.message}`); }
    setSaving(false);
  };
  return (
    <section aria-label="Origen de la solicitud" style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Origen</div>
      <div style={label}>Capturado por el enlace<InfoTip text="Lo que traía el enlace al abrir el formulario. Indica qué enlace se usó, no quién lo compartió: si alguien lo reenvió, figura el enlace original." /></div>
      {o.captured ? (
        <>
          <div style={row}><b>{o.captured.channel || 'Enlace'}</b>{o.captured.campaign && <span>· {o.captured.campaign}</span>}<span style={muted}>· {METHOD_LABELS[o.captured.method]}</span></div>
          {link && <div style={row}><span style={muted}>Enlace:</span> {fieldOf(link, LINK_FIELDS.name) || fieldOf(link, LINK_FIELDS.code)}</div>}
          <div style={row}><span style={muted}>Abierto el</span> {fmt(o.captured.at)}</div>
          {Object.keys(o.captured.params).length > 0 && <div style={{ ...muted, fontFamily: 'monospace', wordBreak: 'break-all' }}>{Object.entries(o.captured.params).map(([k, v]) => `${k}=${v}`).join(' · ')}</div>}
        </>
      ) : <div style={row}><b>{UNKNOWN}</b><span style={muted}>{o.recorded ? '· llegó sin un enlace identificado' : '· enviada antes de que Oike registrara el origen'}</span></div>}

      <div style={{ ...label, marginTop: 10 }}>Lo que declaró la persona<InfoTip text="Su respuesta a “¿Cómo llegaste a mí?”. Se guarda aparte y no se usa para atribuir la solicitud a un canal." /></div>
      <div style={row}>{o.declared ? `“${o.declared}”` : <span style={muted}>No respondió</span>}</div>

      <div style={{ ...label, marginTop: 10 }}>Registrado a mano</div>
      {o.manual ? (
        <div style={row}><b>{o.manual.label}</b>{o.manual.note && <span>· {o.manual.note}</span>}<span style={muted}>· {fmt(o.manual.at)}</span></div>
      ) : !editing && <div style={row}><span style={muted}>Nada todavía.</span></div>}
      {!editing ? (
        <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12, marginTop: 6 }} onClick={() => setEditing(true)}>{o.manual ? 'Cambiar el registro manual' : 'Registrar origen a mano'}</button>
      ) : (
        <div style={{ display: 'grid', gap: 8, marginTop: 6 }}>
          <label style={{ fontSize: 12 }}>Tipo
            <select className="input-field" style={{ display: 'block', width: '100%', marginTop: 4 }} value={kind} onChange={e => setKind(e.target.value)}>
              {Object.entries(MANUAL_ORIGINS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12 }}>Nota (opcional)
            <input className="input-field" style={{ display: 'block', width: '100%', marginTop: 4 }} maxLength={NOTE_MAX} value={note} onChange={e => setNote(e.target.value)} placeholder="Ej.: me escribió por mensaje directo después de un video" />
          </label>
          <div style={muted}>No cambia lo capturado por el enlace ni lo que declaró la persona.</div>
          {error && <div role="alert" style={{ fontSize: 12, color: '#B4362C' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={saving} onClick={save}>{saving ? 'Guardando…' : 'Guardar'}</button>
            <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setEditing(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </section>
  );
}

/** The person of the request: their first origin (never overwritten) and their other requests. */
export function RequestPersonPanel({ request, stakeholders, intakes }) {
  const stkId = ids(request, 'Stakeholder')[0];
  const s = stkId ? stakeholders.find(x => x.id === stkId) : null;
  if (!s) return <section style={card}><div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Persona</div><div style={muted}>La solicitud no tiene un contacto vinculado.</div></section>;
  const first = firstOriginOf(s.fields || {});
  const theirs = intakes.filter(r => ids(r, 'Stakeholder').includes(s.id));
  const isFirst = first.requestId === request.id;
  return (
    <section aria-label="Persona" style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Persona</div>
      <div style={row}><b>{personName(s)}</b>{fieldOf(s, 'Email') && <span style={muted}>· {fieldOf(s, 'Email')}</span>}</div>
      <div style={row}><span style={muted}>Primer origen:</span> <b>{first.label}</b>{first.known && <span style={muted}>· {first.methodLabel}</span>}</div>
      <div style={muted}>{isFirst ? 'Esta solicitud fue su primer contacto.' : theirs.length > 1 ? `Ya existía: esta es una de sus ${theirs.length} solicitudes. Se agregó como interacción, sin cambiar su primer origen.` : 'El contacto ya existía antes de esta solicitud; su primer origen no cambia.'}</div>
      <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12, marginTop: 8 }} onClick={() => openContact(s.id)}>Abrir la ficha del contacto</button>
    </section>
  );
}

/** Opportunities created from this request keep it as their origin; a new one can be created here. */
export function RequestOpportunityPanel({ request, opportunities, api, onAddRecord }) {
  const linked = opportunities.filter(o => ids(o, OPPORTUNITY_FIELDS.request).includes(request.id));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const create = async () => {
    // One request usually gives one opportunity: a second one is created only on purpose
    if (linked.length && !window.confirm('Esta solicitud ya tiene una oportunidad. ¿Crear otra con el mismo origen?')) return;
    setSaving(true); setMsg('');
    const fields = opportunityFieldsFromRequest(request);
    try {
      const rec = await api.createRecord(TABLE_IDS.opportunities, fields);
      if (onAddRecord) onAddRecord('opportunities', fields, rec?.id || null);
      setMsg('Oportunidad creada con el origen de esta solicitud.');
    } catch (e) {
      setMsg(/Origin Request/.test(String(e.message)) ? 'Falta el campo "Origin Request" en Oportunidades (ver el prompt para Omni en docs/solicitudes/origen.md).' : `No se pudo crear: ${e.message}`);
    }
    setSaving(false);
  };
  return (
    <section aria-label="Oportunidad" style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Oportunidad</div>
      {linked.length ? linked.map(o => <div key={o.id} style={row}><b>{fieldOf(o, 'Deal/Opp name') || 'Oportunidad'}</b><span style={muted}>· {fieldOf(o, 'Stage') || '—'} · origen: esta solicitud</span></div>)
        : <div style={muted}>Todavía no hay una oportunidad creada desde esta solicitud.</div>}
      <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12, marginTop: 8 }} disabled={saving} onClick={create}>{saving ? 'Creando…' : linked.length ? 'Crear otra oportunidad' : 'Crear oportunidad con este origen'}</button>
      {msg && <div role="status" style={{ ...muted, marginTop: 6 }}>{msg}</div>}
      <div style={{ ...muted, marginTop: 8 }}>Si la persona empieza un proceso de Acompañamiento, su ficha muestra este mismo primer origen a través del contacto vinculado.</div>
    </section>
  );
}

/** Identified links to the public form: create, copy, and how many requests arrived with each. */
export function RequestLinks({ links, intakes, stakeholders = [], topics = [], events = [], api, onAddRecord, onUpdateRecord, onDeleteRecord }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const [preset, setPreset] = useState('tiktok-bio');
  const [channel, setChannel] = useState('');
  const [campaign, setCampaign] = useState('');
  const [content, setContent] = useState('');
  const [formType, setFormType] = useState('team');
  const [topic, setTopic] = useState('');
  const [eventId, setEventId] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [copied, setCopied] = useState('');
  const p = LINK_PRESETS.find(x => x.key === preset);
  const active = activeTopics(topics);
  const upcoming = events.filter(e => !e.fields?.['Starting'] || new Date(e.fields['Starting']) >= new Date(Date.now() - 864e5))
    .sort((a, b) => String(a.fields?.['Starting'] || '').localeCompare(String(b.fields?.['Starting'] || '')));
  const eventName = (id) => { const e = events.find(x => x.id === id); return e ? fieldOf(e, 'Event Name') || 'Evento' : ''; };
  const base = linkParams({ preset, channel, campaign, content, formType, topic });
  const draft = !base.error && formType === 'webinar' && !eventId ? { error: 'Elegí el webinar al que lleva este enlace.' } : base;
  const identified = useMemo(() => links.filter(l => fieldOf(l, LINK_FIELDS.code)), [links]);
  const codeTaken = !draft.error && identified.some(l => fieldOf(l, LINK_FIELDS.code) === draft.code);
  const typeOf = (l) => (FORM_TYPES[fieldOf(l, LINK_FORM_FIELDS.formType)] ? fieldOf(l, LINK_FORM_FIELDS.formType) : 'team');
  const urlOf = (l) => linkUrl(origin, { utm_source: fieldOf(l, LINK_FIELDS.utmSource), utm_medium: fieldOf(l, LINK_FIELDS.utmMedium), utm_campaign: fieldOf(l, LINK_FIELDS.utmCampaign), utm_content: fieldOf(l, LINK_FIELDS.utmContent), code: fieldOf(l, LINK_FIELDS.code), oike_form: typeOf(l) === 'team' ? '' : typeOf(l), oike_topic: fieldOf(l, LINK_FORM_FIELDS.topic) });
  const copy = async (text, id) => { try { await navigator.clipboard.writeText(text); setCopied(id); setTimeout(() => setCopied(''), 2000); } catch { window.prompt('Copiá el enlace:', text); } };
  /** Deletes the link only (with confirmation): its requests keep the channel and campaign they captured. */
  const remove = async (l, requests) => {
    const name = fieldOf(l, LINK_FIELDS.name) || fieldOf(l, LINK_FIELDS.code);
    const msg = `¿Borrar el enlace "${name}"?\n\n` + (requests ? `${requests === 1 ? 'Una solicitud se envió' : `${requests} solicitudes se enviaron`} con él: se conservan, con el canal y la campaña que traían, pero dejan de figurar en este enlace. ` : '')
      + 'Si el enlace sigue publicado, quien lo use llega igual al formulario, pero su solicitud ya no se identifica con este enlace. No se puede deshacer desde Oike.';
    if (!window.confirm(msg)) return;
    setMsg('');
    try {
      const res = await fetch('/api/intake-review', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ action: 'deleteLink', id: l.id, confirm: true }) });
      if (!res.ok) throw new Error(res.status === 403 ? 'Solo la administración de Oike puede borrar enlaces.' : 'No se pudo borrar el enlace.');
      if (onDeleteRecord) onDeleteRecord('sources', l.id);
      setMsg(`Enlace "${name}" borrado.`);
    } catch (e) { setMsg(e.message); }
  };
  const create = async () => {
    if (draft.error || codeTaken) return;
    setSaving(true); setMsg('');
    const fields = {
      [LINK_FIELDS.name]: draft.label, [LINK_FIELDS.code]: draft.code, [LINK_FIELDS.channel]: draft.channel,
      [LINK_FIELDS.utmSource]: draft.utm_source, [LINK_FIELDS.utmMedium]: draft.utm_medium, [LINK_FIELDS.utmCampaign]: draft.utm_campaign,
      [LINK_FIELDS.destination]: FORM_PATH,
    };
    if (draft.utm_content) fields[LINK_FIELDS.utmContent] = draft.utm_content;
    fields[LINK_FORM_FIELDS.formType] = formType;
    if (draft.oike_topic) fields[LINK_FORM_FIELDS.topic] = draft.oike_topic;
    if (formType === 'webinar' && eventId) fields[LINK_FORM_FIELDS.event] = [eventId];
    try {
      const rec = await api.createRecord(TABLE_IDS.sources, fields);
      if (onAddRecord) onAddRecord('sources', fields, rec?.id || null);
      setMsg('Enlace creado. Ya lo podés copiar.'); setCampaign(''); setContent(''); setChannel('');
    } catch (e) {
      setMsg(/UNKNOWN_FIELD|Unknown field/i.test(String(e.message)) ? 'Faltan campos en la tabla Source de Airtable (ver el prompt para Omni en docs/solicitudes/origen.md).' : `No se pudo crear: ${e.message}`);
    }
    setSaving(false);
  };
  return (
    <div>
      <div style={{ ...card, background: '#F7FBFA' }}>
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          Cada enlace abre el formulario de solicitudes ({FORM_PATH}) con parámetros UTM y un código. Oike solo registra el enlace <b>cuando alguien envía el formulario</b>: no cuenta visitas ni clics. Si alguien reenvía el enlace, la solicitud figura con el enlace original, que no indica quién lo compartió. Los parámetros nunca llevan datos personales.
        </div>
      </div>

      <section aria-label="Nuevo enlace" style={card}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Nuevo enlace identificado</div>
        <div className="form-row">
          <div className="form-field"><label htmlFor="ln-preset">Dónde lo vas a usar</label>
            <select id="ln-preset" className="input-field" value={preset} onChange={e => setPreset(e.target.value)}>{LINK_PRESETS.map(x => <option key={x.key} value={x.key}>{x.label}</option>)}</select>
          </div>
          {p?.askChannel && <div className="form-field"><label htmlFor="ln-channel">Canal *</label><input id="ln-channel" className="input-field" value={channel} onChange={e => setChannel(e.target.value)} placeholder="Ej.: instagram" /></div>}
          {p?.askCampaign && <div className="form-field"><label htmlFor="ln-campaign">Campaña{p.askChannel ? ' (opcional)' : ' *'}</label><input id="ln-campaign" className="input-field" value={campaign} onChange={e => setCampaign(e.target.value)} placeholder="Ej.: lanzamiento-octubre" /></div>}
          <div className="form-field"><label htmlFor="ln-form">Para qué es<InfoTip text="Equipo: el formulario de siempre. Individual: acompañamiento personal, sin empresa ni equipo obligatorios. Webinar: inscripción, y la persona queda como invitada del evento." /></label>
            <select id="ln-form" className="input-field" value={formType} onChange={e => setFormType(e.target.value)}>{Object.entries(FORM_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
          </div>
          <div className="form-field"><label htmlFor="ln-topic">Tema (opcional)</label>
            <select id="ln-topic" className="input-field" value={topic} onChange={e => setTopic(e.target.value)}>
              <option value="">Sin tema</option>
              {active.map(tp => <option key={tp.code} value={tp.code}>{tp.name}</option>)}
            </select>
          </div>
          {formType === 'webinar' && <div className="form-field"><label htmlFor="ln-event">Webinar *</label>
            <select id="ln-event" className="input-field" value={eventId} onChange={e => setEventId(e.target.value)}>
              <option value="">Elegí un evento…</option>
              {upcoming.map(ev => <option key={ev.id} value={ev.id}>{fieldOf(ev, 'Event Name') || 'Evento'}{ev.fields?.['Starting'] ? ` · ${eventWhen(ev.fields['Starting'])}` : ''}</option>)}
            </select>
            {!upcoming.length && <div style={muted}>No hay eventos próximos: crealo primero en Más › Eventos.</div>}
          </div>}
          <div className="form-field"><label htmlFor="ln-content">Detalle (opcional)<InfoTip text="Para distinguir dos enlaces del mismo canal, por ejemplo dos publicaciones. Solo letras, números y guiones; nunca nombres ni emails." /></label><input id="ln-content" className="input-field" value={content} onChange={e => setContent(e.target.value)} placeholder="Ej.: video-prospeccion" /></div>
        </div>
        {draft.error ? <div style={muted}>{draft.error}</div> : (
          <div style={{ fontSize: 12, wordBreak: 'break-all', fontFamily: 'monospace', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>{linkUrl(origin, draft)}</div>
        )}
        {codeTaken && <div style={{ fontSize: 12, color: '#9A6212', marginTop: 6 }}>Ya existe un enlace con este código: copialo de la lista o agregá un detalle.</div>}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button type="button" className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={saving || !!draft.error || codeTaken || CURRENT_USER?.role === 'bdr'} onClick={create}>{saving ? 'Creando…' : 'Crear enlace'}</button>
        </div>
        {msg && <div role="status" style={{ ...muted, marginTop: 6 }}>{msg}</div>}
      </section>

      <RequestTopics topics={topics} api={api} onAddRecord={onAddRecord} onUpdateRecord={onUpdateRecord} />

      <section aria-label="Enlaces identificados" style={card}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Enlaces ({identified.length})</div>
        {!identified.length && <div style={muted}>Todavía no hay enlaces identificados.</div>}
        {identified.map(l => {
          const url = urlOf(l);
          const requests = intakes.filter(r => ids(r, REQUEST_FIELDS.link).includes(l.id)).length;
          const people = newPeopleOfLink(l.id, intakes, stakeholders).length;
          return (
            <div key={l.id} style={{ borderTop: '1px solid #e2e8f0', padding: '10px 0' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', justifyContent: 'space-between' }}>
                <div><b>{fieldOf(l, LINK_FIELDS.name) || fieldOf(l, LINK_FIELDS.code)}</b> <span style={muted}>· {fieldOf(l, LINK_FIELDS.channel) || '—'}{fieldOf(l, LINK_FORM_FIELDS.topic) ? ` · ${topicName(fieldOf(l, LINK_FORM_FIELDS.topic), topics)}` : ''}{ids(l, LINK_FORM_FIELDS.event)[0] ? ` · ${eventName(ids(l, LINK_FORM_FIELDS.event)[0])}` : ''}</span></div>
                <span style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12 }} onClick={() => copy(url, l.id)}>{copied === l.id ? 'Copiado' : 'Copiar enlace'}</button>
                  {CURRENT_USER?.role !== 'bdr' && <button type="button" className="action-btn btn-danger" style={{ fontSize: 12 }} aria-label={`Borrar el enlace ${fieldOf(l, LINK_FIELDS.name) || fieldOf(l, LINK_FIELDS.code)}`} onClick={() => remove(l, requests)}>Borrar</button>}
                </span>
              </div>
              <div style={{ fontSize: 12, wordBreak: 'break-all', fontFamily: 'monospace', color: '#475569', margin: '4px 0' }}>{url}</div>
              <div style={muted}>{typeOf(l) === 'webinar' ? 'Inscripciones' : 'Solicitudes'} enviadas con este enlace: {requests} · personas nuevas: {people}</div>
              {typeOf(l) === 'webinar' && !ids(l, LINK_FORM_FIELDS.event)[0] && <div role="alert" style={{ fontSize: 12, color: '#9B2C20', marginTop: 4 }}>Este enlace de webinar no tiene evento: las inscripciones se guardan, pero sin webinar ni invitación.</div>}
            </div>
          );
        })}
      </section>
    </div>
  );
}

/** The fixed list of topics (table "Request Topics"): add one, or archive it so new links stop offering it. */
function RequestTopics({ topics, api, onAddRecord, onUpdateRecord }) {
  const [name, setName] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const active = activeTopics(topics);
  const code = topicCode(name);
  const taken = code && topics.some(r => fieldOf(r, TOPIC_FIELDS.code) === code);
  const add = async () => {
    if (!code || taken) return;
    setBusy(true); setMsg('');
    const fields = { [TOPIC_FIELDS.name]: name.trim().slice(0, 80), [TOPIC_FIELDS.code]: code, [TOPIC_FIELDS.active]: true };
    try { const rec = await api.createRecord(TABLE_IDS.requestTopics, fields); if (onAddRecord) onAddRecord('requestTopics', fields, rec?.id || null); setName(''); }
    catch (e) { setMsg(/404|NOT_FOUND|TABLE/i.test(String(e.message)) ? 'Falta la tabla "Request Topics" en Airtable (docs/solicitudes/origen.md).' : `No se pudo guardar: ${e.message}`); }
    setBusy(false);
  };
  const archive = async (t) => {
    if (!window.confirm(`¿Archivar el tema "${t.name}"? Los enlaces y solicitudes que ya lo tienen lo conservan; los nuevos enlaces ya no lo ofrecen.`)) return;
    try { await api.updateRecord(TABLE_IDS.requestTopics, t.id, { [TOPIC_FIELDS.active]: false }); if (onUpdateRecord) onUpdateRecord('requestTopics', t.id, { [TOPIC_FIELDS.active]: false }); }
    catch (e) { setMsg(`No se pudo archivar: ${e.message}`); }
  };
  return (
    <section aria-label="Temas" style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Temas ({active.length})</div>
      <div style={{ ...muted, marginBottom: 8 }}>Una lista fija, para que el mismo tema se escriba siempre igual y se pueda comparar.</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {active.map(t => (
          <span key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 4px 3px 10px', borderRadius: 999, background: '#E6F2EF', color: '#155C54' }}>
            {t.name}
            <button type="button" aria-label={`Archivar el tema ${t.name}`} onClick={() => archive(t)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#155C54', fontSize: 13, padding: '0 4px' }}>×</button>
          </span>
        ))}
        {!active.length && <span style={muted}>Todavía no hay temas.</span>}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="input-field" style={{ flex: '1 1 220px' }} aria-label="Nuevo tema" placeholder="Nuevo tema, por ejemplo: Prospección" value={name} onChange={e => setName(e.target.value)} />
        <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12 }} disabled={busy || !code || taken} onClick={add}>Agregar tema</button>
      </div>
      {name && code && <div style={{ ...muted, marginTop: 4 }}>Código en el enlace: <code>{code}</code>{taken ? ' · ya existe' : ''}</div>}
      {msg && <div role="status" style={{ ...muted, marginTop: 6 }}>{msg}</div>}
    </section>
  );
}

/** For the contact card: first origin (completed by hand only while unknown) and the origin of each request. */
export function PersonOriginPanel({ stakeholder, intakes = [], api, onUpdateRecord }) {
  const [saved, setSaved] = useState(null); // shown right away, before the data reloads
  const first = firstOriginOf({ ...(stakeholder?.fields || {}), ...(saved || {}) });
  const theirs = intakes.filter(r => ids(r, 'Stakeholder').includes(stakeholder?.id))
    .sort((a, b) => String(fieldOf(a, REQUEST_FIELDS.capturedAt) || a.createdTime || '').localeCompare(String(fieldOf(b, REQUEST_FIELDS.capturedAt) || b.createdTime || '')));
  const [editing, setEditing] = useState(false);
  const [kind, setKind] = useState('own_prospecting');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  if (!stakeholder) return null;
  const save = async () => {
    setSaving(true); setError('');
    const fields = manualOriginFields({ kind, note }, 'person');
    try {
      await api.updateRecord(TABLE_IDS.stakeholders, stakeholder.id, fields);
      if (onUpdateRecord) onUpdateRecord('stakeholders', stakeholder.id, fields);
      setSaved(fields); setEditing(false);
    } catch (e) { setError(/Unknown field|UNKNOWN_FIELD/i.test(String(e.message)) ? 'Faltan los campos de origen en Stakeholders (docs/solicitudes/origen.md).' : `No se pudo guardar: ${e.message}`); }
    setSaving(false);
  };
  return (
    <div style={{ border: '1px solid var(--globant-border)', borderRadius: 10, padding: '12px 14px', margin: '12px 0', background: 'var(--globant-card)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--globant-muted)' }}>Origen</div>
      <div style={{ fontSize: 14, margin: '4px 0' }}><b>{first.label}</b>{first.known && <span style={{ color: 'var(--globant-muted)', fontSize: 12 }}> · {first.methodLabel}{first.at ? ` · ${fmt(first.at)}` : ''}</span>}</div>
      {first.note && <div style={{ fontSize: 12, color: 'var(--globant-muted)' }}>{first.note}</div>}
      {theirs.length > 0 && (
        <div style={{ fontSize: 12, color: 'var(--globant-muted)', marginTop: 4 }}>
          {theirs.length === 1 ? 'Una solicitud' : `${theirs.length} solicitudes`}: {theirs.map(r => requestOriginLabel(r.fields || {})).join(' · ')}
        </div>
      )}
      {first.canRecord && !editing && <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12, marginTop: 8 }} onClick={() => setEditing(true)}>Registrar origen a mano</button>}
      {editing && (
        <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
          <label style={{ fontSize: 12 }}>Tipo
            <select className="input-field" style={{ display: 'block', width: '100%', marginTop: 4 }} value={kind} onChange={e => setKind(e.target.value)}>
              {Object.entries(MANUAL_ORIGINS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12 }}>Nota (opcional)
            <input className="input-field" style={{ display: 'block', width: '100%', marginTop: 4 }} maxLength={NOTE_MAX} value={note} onChange={e => setNote(e.target.value)} placeholder="Ej.: la contacté por LinkedIn" />
          </label>
          <div style={{ fontSize: 12, color: 'var(--globant-muted)' }}>Queda como su primer origen. Una vez registrado, no se reemplaza.</div>
          {error && <div role="alert" style={{ fontSize: 12, color: '#B4362C' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="action-btn btn-primary" style={{ fontSize: 12 }} disabled={saving} onClick={save}>{saving ? 'Guardando…' : 'Guardar'}</button>
            <button type="button" className="action-btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setEditing(false)}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}
